/**
 * @file components/chat/WelcomeModelSelector.jsx
 * Uses useChatSession
 */
import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useChatSession } from '../../context/ChatSessionContext';
import { PROPRIETARY_MODELS, PROVIDER_GROUPS, OTHER_GROUP_LABEL, MODEL_PRICING, MODEL_TIER } from '../../config';
import styles from '../../styles/ChatApp.module.css';
import ModelIcon from '../common/ModelIcon';

const WelcomeModelSelector = () => {
    const { selectedModel, setSelectedModel } = useChatSession();

    // Build the grouped list entirely from PROVIDER_GROUPS config.
    const claimedByPrefix = new Set(
        PROVIDER_GROUPS.flatMap(({ prefix, models }) =>
            models ? models : (prefix ? PROPRIETARY_MODELS.filter(m => m.startsWith(prefix)) : [])
        )
    );

    const grouped = [
        ...PROVIDER_GROUPS.map(({ label, prefix, models }) => ({
            label,
            models: models ?? PROPRIETARY_MODELS.filter(m => m.startsWith(prefix)),
        })),
        {
            label: OTHER_GROUP_LABEL,
            models: PROPRIETARY_MODELS.filter(m => !claimedByPrefix.has(m)),
        },
    ].filter(g => g.models.length > 0);

    // Accordion state — all groups open by default
    const [expandedGroups, setExpandedGroups] = useState(
        () => new Set(grouped.map(g => g.label))
    );

    const [openTooltip, setOpenTooltip] = useState(null);

    const TIER_LABEL = { Light: '$', Standard: '$$', Premium: '$$$' };

    const handlePriceClick = (e, model) => {
        e.stopPropagation();
        setOpenTooltip(prev => prev === model ? null : model);
    };

    const toggleGroup = (label) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(label)) next.delete(label);
            else next.add(label);
            return next;
        });
    };

    return (
        <div className={styles.welcomeSelectorContainer} onClick={() => setOpenTooltip(null)}>
            <h2 className={styles.welcomeSelectorTitle}>How would you like to start?</h2>

            {/* Route AI — hero card */}
            <div
                className={`${styles.routeAICard} ${selectedModel === 'Route AI' ? styles.activeRouteAICard : ''}`}
                onClick={() => setSelectedModel('Route AI')}
            >
                <div className={styles.cardIcon}>
                    <ModelIcon modelName="Route AI" size={32} />
                </div>
                <div className={styles.cardBody}>
                    <div className={styles.cardTitleRow}>
                        <p className={styles.cardTitle}>Route AI</p>
                        <span className={styles.recommendedBadge}>Recommended</span>
                    </div>
                    <p className={styles.cardSubtitle}>Automatically picks the best model for every task.</p>
                </div>
                {selectedModel === 'Route AI' && (
                    <span className={styles.activeCheckIcon}>✓</span>
                )}
            </div>

            {/* Provider groups */}
            <div className={styles.providerGrid}>
                {grouped.map(({ label, models }) => {
                    const isExpanded = expandedGroups.has(label);
                    return (
                        <div key={label} className={styles.providerGroup}>
                            <button
                                className={styles.providerGroupHeader}
                                onClick={() => toggleGroup(label)}
                                aria-expanded={isExpanded}
                            >
                                <span className={styles.providerLabel}>{label}</span>
                                <ChevronDown
                                    size={14}
                                    className={`${styles.chevronIcon} ${isExpanded ? styles.chevronOpen : ''}`}
                                />
                            </button>
                            {isExpanded && models.map(model => {
                                const tier = MODEL_TIER[model];
                                const pricing = MODEL_PRICING[model];
                                return (
                                    <div
                                        key={model}
                                        className={`${styles.welcomeModelCard} ${selectedModel === model ? styles.activeWelcomeCard : ''}`}
                                        onClick={() => setSelectedModel(model)}
                                    >
                                        <div className={styles.cardIcon}>
                                            <ModelIcon modelName={model} size={20} />
                                        </div>
                                        <div className={styles.modelCardBody}>
                                            <p className={styles.cardTitle}>{model}</p>
                                        </div>
                                        {tier && (
                                            <div className={styles.modelPriceWrapper}>
                                                <button
                                                    className={`${styles.modelPriceLabel} ${styles[`modelPrice${tier}`]}`}
                                                    onClick={(e) => handlePriceClick(e, model)}
                                                    aria-label={`Pricing for ${model}`}
                                                >
                                                    {TIER_LABEL[tier]}
                                                </button>
                                                {openTooltip === model && pricing && (
                                                    <div className={styles.modelPricingTooltip} onClick={e => e.stopPropagation()}>
                                                        <span className={styles.modelPricingTooltipLabel}>API cost · $ cheap &nbsp;$$$ premium</span>
                                                        <span className={styles.modelPricingTooltipValue}>${pricing.input} in · ${pricing.output} out &nbsp;/1M tokens</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {selectedModel === model && (
                                            <span className={styles.activeCheckIcon}>✓</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default WelcomeModelSelector;
